import { useState } from "react";
import { makeEconomy, upsertTrip, deleteTrip, newId, fuelLogsForCar, type CanonicalStore, type Car, type Trip } from "../../core/index.ts";
import { Modal, NumberField, TextField, SelectField, DateField, FormActions, parseNum, numToStr } from "./Modal.tsx";
import { epochToDateInput, dateInputToEpoch, longDate } from "../format.ts";
import { IconPin } from "../components/Icons.tsx";

const PURPOSES = ["Personal", "Business", "Medical"];

export function TripForm({ store, car, editing, onSave, onClose }: {
  store: CanonicalStore; car: Car; editing?: Trip; onSave: (s: CanonicalStore) => void; onClose: () => void;
}) {
  const economy = makeEconomy(store.profile.unitSystem === "metric");
  const dl = economy.distanceLabel();
  const currency = store.profile.displayCurrency;

  const [start, setStart] = useState(editing ? numToStr(round1(economy.convertDistance(editing.startOdometer))) : "");
  const [end, setEnd] = useState(editing ? numToStr(round1(economy.convertDistance(editing.endOdometer))) : "");
  const [from, setFrom] = useState(editing?.startLocationName ?? "");
  const [to, setTo] = useState(editing?.endLocationName ?? "");
  const [waypoints, setWaypoints] = useState<string[]>(editing?.waypoints ?? []);
  const [purpose, setPurpose] = useState(editing?.purpose ?? "Personal");
  const [fuelCost, setFuelCost] = useState(editing?.fuelCost != null ? numToStr(editing.fuelCost) : "");
  const [tollCost, setToll] = useState(editing?.tollCost != null ? numToStr(editing.tollCost) : "");
  const [parkingCost, setParking] = useState(editing?.parkingCost != null ? numToStr(editing.parkingCost) : "");
  const [dateStr, setDate] = useState(epochToDateInput(editing?.date ?? null));
  const [fuelLogIds, setFuelLogIds] = useState<string[]>(editing?.fuelLogIds ?? []);

  const startOdoKm = economy.toKm(parseNum(start));
  const endOdoKm = economy.toKm(parseNum(end));
  const tripEpoch = dateInputToEpoch(dateStr);

  const logs = fuelLogsForCar(store.fuelLogs, car.id)
    .filter((l) => {
      const odo = l.odometer;
      const dateOk = tripEpoch ? (l.date ?? 0) >= tripEpoch : true;
      const odoOk = parseNum(start) > 0 && parseNum(end) > 0 ? odo >= startOdoKm && odo <= endOdoKm : true;
      return dateOk && odoOk;
    })
    .sort((a, b) => (b.date ?? 0) - (a.date ?? 0));

  const addWaypoint = () => setWaypoints([...waypoints, ""]);
  const updateWaypoint = (i: number, v: string) => {
    const next = [...waypoints];
    next[i] = v;
    setWaypoints(next);
  };
  const removeWaypoint = (i: number) => setWaypoints(waypoints.filter((_, idx) => idx !== i));

  const toggleFuelLog = (id: string) => {
    setFuelLogIds(fuelLogIds.includes(id) ? fuelLogIds.filter((x) => x !== id) : [...fuelLogIds, id]);
  };

  const hasValidOdo = parseNum(end) >= parseNum(start) && parseNum(start) > 0;
  const canSave = start !== "" && end !== "" && from.trim() !== "" && to.trim() !== "" && hasValidOdo;

  const buildMapsUrl = () => {
    if (!from.trim() || !to.trim()) return null;
    const params = new URLSearchParams();
    params.set("api", "1");
    params.set("origin", from.trim());
    params.set("destination", to.trim());
    if (waypoints.filter((w) => w.trim()).length > 0) {
      params.set("waypoints", waypoints.filter((w) => w.trim()).join("|"));
    }
    params.set("travelmode", "driving");
    return `https://www.google.com/maps/dir/?${params.toString()}`;
  };

  const mapsUrl = buildMapsUrl();

  return (
    <Modal title={editing ? "Edit trip" : "New trip"} onClose={onClose}>
      <div className="eyebrow amber">ROUTE</div>
      <NumberField label={`Start odometer (${dl})`} value={start} onChange={setStart} />
      <NumberField label={`End odometer (${dl})`} value={end} onChange={setEnd} />
      <TextField label="From *" value={from} onChange={setFrom} placeholder="Required" />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8 }}>
        <div className="eyebrow" style={{ fontSize: 9, letterSpacing: 1.6 }}>STOPS (OPTIONAL)</div>
        <button className="btn ghost" style={{ fontSize: 9, padding: "6px 10px" }} onClick={addWaypoint}>+ ADD STOP</button>
      </div>
      {waypoints.map((wp, i) => (
        <div key={i} style={{ display: "flex", gap: 6, marginTop: 6 }}>
          <input
            style={{ flex: 1, background: "var(--panel2)", border: "1px solid var(--edge)", borderRadius: 10, color: "var(--cream)", fontFamily: "var(--mono)", fontSize: 12, padding: "10px 14px" }}
            value={wp} onChange={(e) => updateWaypoint(i, e.target.value)} placeholder={`Stop ${i + 1}`}
          />
          <button className="btn ghost" style={{ fontSize: 9, padding: "6px 10px", borderColor: "var(--redline)", color: "var(--redline)" }} onClick={() => removeWaypoint(i)}>×</button>
        </div>
      ))}

      <TextField label="To *" value={to} onChange={setTo} placeholder="Required" />

      {mapsUrl && (
        <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="linkrow" style={{ marginTop: 12, textDecoration: "none" }}>
          <span className="mono" style={{ fontSize: 10, letterSpacing: 1.6 }}>OPEN IN GOOGLE MAPS</span>
          <span className="mono amber" style={{ fontSize: 12 }}>↗</span>
        </a>
      )}

      <div className="eyebrow amber" style={{ marginTop: 14 }}>DETAILS</div>
      <SelectField label="Purpose" value={purpose} onChange={setPurpose} options={PURPOSES} />
      <NumberField label={`Fuel cost (${currency}, optional)`} value={fuelCost} onChange={setFuelCost} />
      <NumberField label={`Toll (${currency}, optional)`} value={tollCost} onChange={setToll} />
      <NumberField label={`Parking (${currency}, optional)`} value={parkingCost} onChange={setParking} />
      <DateField label="Date" value={dateStr} onChange={setDate} />

      {logs.length > 0 && (
        <>
          <div className="eyebrow amber" style={{ marginTop: 14 }}>LINKED FILL-UPS (OPTIONAL)</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
            {logs.map((l) => {
              const selected = fuelLogIds.includes(l.id);
              return (
                <button
                  key={l.id}
                  className="panel"
                  style={{ cursor: "pointer", textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center", borderColor: selected ? "var(--amber)" : undefined, background: selected ? "color-mix(in srgb, var(--amber) 8%, transparent)" : undefined }}
                  onClick={() => toggleFuelLog(l.id)}
                >
                  <div>
                    <span className="mono cream" style={{ fontSize: 11 }}>{longDate(l.date ?? null)}</span>
                    {l.stationName && <span className="mono muted" style={{ fontSize: 10, marginLeft: 8, display: "inline-flex", alignItems: "center", gap: 3 }}><IconPin size={9} />{l.stationName}</span>}
                  </div>
                  <span className="mono" style={{ fontSize: 11 }}>
                    {l.volumeAdded > 0 && <span className="amber">{economy.convertVolume(l.volumeAdded).toFixed(1)} {economy.volumeLabel()}</span>}
                    {l.kwhAdded != null && l.kwhAdded > 0 && <span className="cyan">{l.kwhAdded.toFixed(1)} kWh</span>}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}

      <FormActions
        canSave={!!canSave}
        onSave={() => {
          const t: Trip = {
            // Spread first so every field this form doesn't expose — coordinates,
            // recorded route, telemetry, passengers, fuel volume/energy/station —
            // survives an edit instead of being silently dropped.
            ...(editing ?? {}),
            id: editing?.id ?? newId(), carId: car.id, date: dateInputToEpoch(dateStr),
            startOdometer: economy.toKm(parseNum(start)), endOdometer: economy.toKm(parseNum(end)),
            startLocationName: from.trim(), endLocationName: to.trim(),
            purpose, fuelCost: fuelCost ? parseNum(fuelCost) : null,
            tollCost: tollCost ? parseNum(tollCost) : null, parkingCost: parkingCost ? parseNum(parkingCost) : null,
            waypoints: waypoints.filter((w) => w.trim()),
            polyline: editing?.polyline ?? [],
            fuelLogIds, notes: editing?.notes ?? null,
            mileageRate: editing?.mileageRate ?? store.profile.mileageRate ?? null,
          };
          onSave(upsertTrip(store, t)); onClose();
        }}
        onDelete={editing ? () => { onSave(deleteTrip(store, editing.id)); onClose(); } : undefined}
      />
    </Modal>
  );
}

const round1 = (v: number) => Math.round(v * 10) / 10;
